import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { GeneratedPost, isPastelUri, parsePastelUri } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';

const AVATAR_URL = '/novah_laugh.png';
const API_BASE_URL = import.meta.env?.VITE_API_URL || 'https://agency-smm-api.carlos-mdt-1057.workers.dev';
const proxyImg = (url: string) => `${API_BASE_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;

/* ── Animations ── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;

const blobA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(12px, -16px) scale(1.12); }
  50% { transform: translate(-8px, 12px) scale(0.95); }
  75% { transform: translate(16px, 8px) scale(1.08); }
`;

const blobB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1.1); }
  25% { transform: translate(-16px, 8px) scale(0.92); }
  50% { transform: translate(12px, -12px) scale(1.15); }
  75% { transform: translate(-8px, -16px) scale(1); }
`;

const blobC = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(20px, 12px) scale(1.1); }
  66% { transform: translate(-12px, -8px) scale(0.92); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.04); }
`;

const sheenRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

/* ── Layout ── */
const HubWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg} 0;
  animation: ${fadeIn} 0.4s ease-out;
  max-width: 700px;
  margin: 0 auto;
`;

/* ── Orb + Avatar ── */
const AvatarWrapper = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  animation: ${popIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
`;

const OrbAmbient = styled.div`
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(180,120,255,0.12) 0%, rgba(100,180,255,0.06) 50%, transparent 70%);
  animation: ${glowPulse} 4s ease-in-out infinite;
`;

const OrbSphere = styled.div`
  position: absolute;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  overflow: hidden;
  z-index: 1;
  box-shadow: 0 0 30px rgba(180,100,255,0.2), 0 0 60px rgba(140,80,230,0.08);
`;

const Blob = styled.div<{ $color: string; $size: number; $top: string; $left: string }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  filter: blur(20px);
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
`;
const BlobA = styled(Blob)` animation: ${blobA} 8s ease-in-out infinite; `;
const BlobB = styled(Blob)` animation: ${blobB} 10s ease-in-out infinite; `;
const BlobC = styled(Blob)` animation: ${blobC} 7s ease-in-out infinite; `;

const OrbSheen = styled.div`
  position: absolute;
  width: 170px; height: 170px;
  border-radius: 50%;
  z-index: 3;
  pointer-events: none;
  background: radial-gradient(circle at 30% 25%, rgba(255,255,255,0.25) 0%, transparent 20%);
  animation: ${sheenRotate} 25s linear infinite;
`;

const OrbRim = styled.div`
  position: absolute;
  width: 170px; height: 170px;
  border-radius: 50%;
  z-index: 4;
  pointer-events: none;
  border: 1px solid rgba(255,255,255,0.12);
`;

const Avatar = styled.div`
  position: relative;
  width: 128px; height: 128px;
  border-radius: 50%;
  overflow: hidden;
  z-index: 5;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  mask-image: radial-gradient(circle, #000 65%, rgba(0,0,0,0.4) 82%, transparent 100%);
  -webkit-mask-image: radial-gradient(circle, #000 65%, rgba(0,0,0,0.4) 82%, transparent 100%);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

/* ── Text ── */
const Greeting = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin: 0 0 4px 0;
`;

const SubText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: 1.5;
`;

/* ── Post Carousel ── */
const CarouselSection = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CarouselLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const CarouselStrip = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 4px 0 8px 0;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 4px; }
`;

const PostThumb = styled.div`
  flex-shrink: 0;
  width: 100px;
  height: 140px;
  border-radius: 10px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surfaceHover};
  cursor: pointer;
  scroll-snap-align: start;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  position: relative;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ThumbBadge = styled.span`
  position: absolute;
  bottom: 4px;
  left: 4px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: rgba(0,0,0,0.55);
  padding: 1px 5px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const EmptyCarousel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

/* ── Quick Actions ── */
const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ActionBtn = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: ${({ $primary }) => $primary ? 'none' : '1px solid #E2E8F0'};
  background: ${({ $primary }) => $primary
    ? 'linear-gradient(135deg, #3B82F6, #8B5CF6)'
    : '#fff'};
  color: ${({ $primary }) => $primary ? '#fff' : '#0F172A'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $primary }) => $primary
    ? '0 4px 14px rgba(59,130,246,0.3)'
    : '0 1px 3px rgba(0,0,0,0.04)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ $primary }) => $primary
      ? '0 6px 20px rgba(59,130,246,0.4)'
      : '0 4px 12px rgba(0,0,0,0.08)'};
  }
`;

/* ── Stats ── */
const StatsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.surfaceHover};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

/* ── Component ── */
interface NovahHubProps {
  workspaceId: string;
  workspaceName: string;
  postsCount?: number;
  assetsCount?: number;
}

export function NovahHub({ workspaceId, workspaceName, postsCount = 0, assetsCount = 0 }: NovahHubProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentPosts, setRecentPosts] = useState<GeneratedPost[]>([]);

  const firstName = user?.name?.split(' ')[0] || 'there';

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const posts = await apiClient.get<GeneratedPost[]>(`/api/workspaces/${workspaceId}/posts?limit=10`);
        setRecentPosts(posts);
      } catch {
        // No posts yet
      }
    };
    fetchPosts();
  }, [workspaceId]);

  const lastPost = recentPosts[0];
  const lastDate = lastPost ? new Date(lastPost.createdAt).toLocaleDateString() : null;

  return (
    <HubWrapper>
      {/* Orb + Avatar */}
      <AvatarWrapper>
        <OrbAmbient />
        <OrbSphere>
          <BlobA $color="rgba(255,130,200,0.5)" $size={100} $top="5%" $left="10%" />
          <BlobB $color="rgba(150,100,255,0.4)" $size={90} $top="35%" $left="45%" />
          <BlobC $color="rgba(80,200,230,0.35)" $size={85} $top="50%" $left="10%" />
        </OrbSphere>
        <OrbSheen />
        <OrbRim />
        <Avatar>
          <img src={AVATAR_URL} alt="Novah" />
        </Avatar>
      </AvatarWrapper>

      {/* Greeting */}
      <Greeting>
        {postsCount > 0 ? `Welcome back, ${firstName}!` : `Hey ${firstName}!`}
      </Greeting>
      <SubText>
        {postsCount > 0
          ? `${workspaceName} has ${postsCount} post${postsCount !== 1 ? 's' : ''} generated.${lastDate ? ` Last batch: ${lastDate}.` : ''}`
          : `Let's create some amazing content for ${workspaceName}!`}
      </SubText>

      {/* Post Carousel */}
      {recentPosts.length > 0 && (
        <CarouselSection>
          <CarouselLabel>Recent Posts</CarouselLabel>
          <CarouselStrip>
            {recentPosts.map((post) => {
              const hasPastel = post.imageUrl && isPastelUri(post.imageUrl);
              const pastelColor = hasPastel ? parsePastelUri(post.imageUrl!) : null;
              const hasStockImg = post.imageUrl && !hasPastel;
              return (
                <PostThumb
                  key={post.id}
                  onClick={() => navigate(`/workspace/${workspaceId}/generate`)}
                  style={pastelColor ? { background: pastelColor } : undefined}
                >
                  {hasStockImg ? (
                    <img src={proxyImg(post.imageUrl!)} alt={post.network} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  ) : !pastelColor ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94A3B8', padding: 8, textAlign: 'center' }}>
                      {post.network}
                    </div>
                  ) : null}
                  <ThumbBadge>{post.network.substring(0, 3)}</ThumbBadge>
                </PostThumb>
              );
            })}
          </CarouselStrip>
        </CarouselSection>
      )}

      {recentPosts.length === 0 && postsCount === 0 && (
        <CarouselSection>
          <EmptyCarousel>No posts yet. Let's generate your first batch!</EmptyCarousel>
        </CarouselSection>
      )}

      {/* Quick Actions */}
      <ActionsGrid>
        <ActionBtn $primary onClick={() => navigate(`/workspace/${workspaceId}/generate`)}>
          ✨ Generate Posts
        </ActionBtn>
        <ActionBtn onClick={() => navigate(`/workspace/${workspaceId}/brand-profile`)}>
          📋 Brand Profile
        </ActionBtn>
        <ActionBtn onClick={() => navigate(`/workspace/${workspaceId}/templates`)}>
          🎨 Templates
        </ActionBtn>
        <ActionBtn onClick={() => navigate(`/workspace/${workspaceId}/setup`)}>
          ⚙️ Brand Setup
        </ActionBtn>
      </ActionsGrid>

      {/* Stats */}
      <StatsRow>
        <StatChip>📝 {postsCount} posts</StatChip>
        <StatChip>🖼️ {assetsCount} assets</StatChip>
        {lastDate && <StatChip>🕐 Last: {lastDate}</StatChip>}
      </StatsRow>
    </HubWrapper>
  );
}
